import type { Session } from '@supabase/supabase-js';
import { normalizePostAuthRedirect } from './postAuthRedirect';
import { needsProfileCompletion } from '../utils/profileHelpers';

export interface ProfileCompletionFunnelContext {
    source?: string;
    projectId?: string;
    intentId?: string;
    activationGeneration?: number;
    finalizationRequestId?: string;
    journeyId?: string;
    processingState?: string;
}

export interface ProfileCompletionDestination {
    destination: string;
    needsProfileCompletion: boolean;
}

const ACTIVATION_ROUTE_PATTERN = /^\/(?:dashboard|digests)\/[^/]+\/activate(?:[/?]|$)/;
const AUTHENTICATED_APP_DESTINATION_PATTERN = /^\/(?:dashboard|digests|account)(?:[/?]|$)/;
const CONTEXTUAL_PAPER_DESTINATION_PATTERN = /^\/(?:p|paper)(?:\/|$)/;

function appendQueryParams(url: string, values: Record<string, string | undefined>): string {
    const [base, existingQuery = ''] = url.split('?');
    const searchParams = new URLSearchParams(existingQuery);

    Object.entries(values).forEach(([key, value]) => {
        if (value) {
            searchParams.set(key, value);
        }
    });

    const query = searchParams.toString();
    return query ? `${base}?${query}` : base;
}

/**
 * Builds the only profile-completion route used after authentication. The
 * exact post-auth destination remains inside redirect_to so onboarding state
 * such as `onboarding=preparing` survives the profile detour.
 */
export function buildProfileCompletionRoute(
    redirectTo?: string,
    funnelContext?: ProfileCompletionFunnelContext
): string {
    const destination = normalizePostAuthRedirect(redirectTo);

    return appendQueryParams('/complete-profile', {
        redirect_to: destination,
        funnel_source: funnelContext?.source,
        funnel_step: 'profile',
        funnel_project_id: funnelContext?.projectId,
        funnel_intent_id: funnelContext?.intentId,
        activation_generation: funnelContext?.activationGeneration?.toString(),
        finalization_request_id: funnelContext?.finalizationRequestId,
        funnel_journey_id: funnelContext?.journeyId,
        processing_state: funnelContext?.processingState,
    });
}

/**
 * Resolves an authenticated handoff through profile completion without losing
 * its server-selected destination.
 */
export function resolveProfileCompletionDestination({
    session,
    destination,
    funnelContext,
}: {
    session: Session | null | undefined;
    destination?: string;
    funnelContext?: ProfileCompletionFunnelContext;
}): ProfileCompletionDestination {
    const normalizedDestination = normalizePostAuthRedirect(destination);
    const requiresProfileCompletion = !isRouteOwnedProfileCompletionPath(normalizedDestination)
        && isAuthenticatedAppDestination(normalizedDestination, funnelContext)
        && needsProfileCompletion(session ?? null);

    return {
        destination: requiresProfileCompletion
            ? buildProfileCompletionRoute(normalizedDestination, funnelContext)
            : normalizedDestination,
        needsProfileCompletion: requiresProfileCompletion,
    };
}

/** Profile completion may interpose only on private app destinations. Public,
 * lifecycle, preference, and checkout destinations must remain uninterrupted. */
export function isAuthenticatedAppDestination(
    destination: string,
    funnelContext?: ProfileCompletionFunnelContext
): boolean {
    if (AUTHENTICATED_APP_DESTINATION_PATTERN.test(destination)) {
        return true;
    }

    const [pathname, query = ''] = destination.split('?');
    if (CONTEXTUAL_PAPER_DESTINATION_PATTERN.test(pathname)) {
        const searchParams = new URLSearchParams(query);
        if (searchParams.get('projectId') && searchParams.get('projectPaperId')) {
            return true;
        }
    }

    return false;
}

/** Activation owns its post-verification navigation while it finalizes the
 * exact onboarding intent. The app-layout fallback must not race it. */
export function isRouteOwnedProfileCompletionPath(pathname: string): boolean {
    return ACTIVATION_ROUTE_PATTERN.test(pathname);
}
